import { Component, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DynamicDialogRef, DynamicDialogConfig, DialogService } from 'primeng/dynamicdialog';
import { Table, TableLazyLoadEvent, TableRowSelectEvent } from 'primeng/table';
import { AssetManagementControllerGetAll$Params } from 'src/app/api/fn/asset-management/asset-management-controller-get-all';
import { buildSerialSearch } from './serial-search';
import {
  chunk,
  classifyPastedSerials,
  parseSerialList,
  SERIAL_PASTE_CHUNK_SIZE,
  SerialPasteReport,
  SerialPasteScope,
} from './serial-paste';
import { AssetManagementItem, AssetManagementListResponse, BaseResponse } from 'src/app/api/models';
import { AssetManagementService, IdentityService } from 'src/app/api/services';
import { AuthService } from 'src/app/services/auth/auth.service';
import { enumAssetManagementPurpose } from '../enums/asset-management.enum';
import {
  catchError,
  concat,
  concatMap,
  debounceTime,
  filter,
  finalize,
  firstValueFrom,
  map,
  merge,
  mergeAll,
  of,
  Subscription,
  switchMap,
  tap,
} from 'rxjs';
import { PaginatorState } from 'primeng/paginator';

@Component({
  selector: 'app-modal-serial-in-stock',
  templateUrl: './modal-serial-in-stock.component.html',
  styleUrls: ['./modal-serial-in-stock.component.scss'],
})
export class ModalSerialInStockComponent {
  protected readonly rowsPerPageOptions = [25, 50, 100, 200, 500];
  assets: AssetManagementItem[] = [];
  totalRecords: number = 0;
  question_title: string;
  warehouse_id?: number;
  product_id?: number;
  related_agreeement_id?: number;
  related_delivery_id: number | null = null;
  // Picker del DDT: solo i seriali assegnati alla consegna (related_delivery_id), che possono non
  // essere più in un magazzino: niente filtro per magazzino, utilizzo e commessa
  onlyDelivery = false;
  loading = false;
  purpose?: enumAssetManagementPurpose;
  selection = false;
  loadedAllAssets = false;
  preselectedAssets: string[] = [];
  selectedAssets: AssetManagementItem[] = [];
  // Quantità massima di seriali per la riga (se il chiamante la indica) e seriali già inseriti
  // a mano, che non fanno parte della selezione di questa finestra. Solo per avvisare.
  qtyLimit?: number;
  manualCount = 0;
  // elenco incollato: testo, esito e stato della ricerca
  pasteOpen = false;
  pasteText = new FormControl<string>('', { nonNullable: true });
  pasting = false;
  pasteError = false;
  pasteReport?: SerialPasteReport;
  // seriali dell'elenco selezionabili ma non selezionati perché si supererebbe la quantità
  pasteOverLimit: string[] = [];
  notFoundCopied = false;
  // ricerca in corso: scrivendo si fanno più chiamate e conta solo l'ultima
  private loadSub?: Subscription;
  // selectedAssetsControl = new FormControl<string[]>([], {nonNullable: true});

  @ViewChild('dt1', { static: true }) table?: Table;

  frm_search = new FormGroup({
    search: new FormControl<string>(''),
    skip: new FormControl<number>(0),
    pageSize: new FormControl<number>(this.rowsPerPageOptions[0]),
  });

  constructor(
    public auth: AuthService,
    private assetMgmtService: AssetManagementService,
    private userService: IdentityService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    public dialogService: DialogService
  ) {
    this.purpose = config.data.purpose;
    this.preselectedAssets = config.data!.selectedAssets || [];
    // this.selectedAssetsControl.patchValue(this.selectedAssets);
    this.selection = config.data!.selection || false;
    this.question_title = config.data!.question_title;
    this.warehouse_id = config.data!.warehouse_id;
    this.product_id = config.data!.product_id;
    this.related_agreeement_id = config.data?.related_agreeement_id;
    this.qtyLimit = config.data?.qty_limit ?? undefined;
    this.manualCount = config.data?.manual_count ?? 0;
    this.onlyDelivery = !!config.data?.only_delivery;
    this.related_delivery_id = config.data!.related_delivery_id || null;
    if (config.data!.related_delivery_id !== null) {
      this.related_delivery_id = Number(config.data!.related_delivery_id);
    }
  }

  ngOnInit() {
    this.frm_search
      .get('search')
      ?.valueChanges.pipe(debounceTime(300))
      .subscribe(() => {
        const { serial_numbers } = buildSerialSearch(this.frm_search.value.search);
        this.frm_search.get('skip')?.setValue(0);
        // con un elenco incollato si mostrano subito tutti i seriali richiesti
        this.frm_search
          .get('pageSize')
          ?.setValue(serial_numbers?.length ?? this.rowsPerPageOptions[0]);
        this.loadAssets();
      });
    if (this.preselectedAssets.length === 0) {
      this.loadedAllAssets = true;
    } else {
      merge(
        this.assetMgmtService
          .assetManagementControllerGetAll({
            limit: this.preselectedAssets.length,
            skip: 0,
            serial_numbers: this.preselectedAssets,
          })
          .pipe(
            catchError(() =>
              of({
                result: false,
                message: '',
                data: {
                  list: [],
                  count: 0,
                },
              } as BaseResponse & {
                'data'?: AssetManagementListResponse;
              })
            )
          )
      )
        .pipe(finalize(() => (this.loadedAllAssets = true)))
        .subscribe({
          next: res => {
            this.selectedAssets.push(...res.data.list);
          },
          error: err => {
            //Ignore error
          },
        });
    }
    this.loadAssets();
  }

  loadAssets() {
    this.loading = true;
    const search = buildSerialSearch(this.frm_search.value.search);
    const params: AssetManagementControllerGetAll$Params = {
      limit: this.frm_search.get('pageSize')?.value!,
      skip: this.frm_search.get('skip')?.value!,
      related_product_id: this.product_id,
      serial_number: search.serial_number,
      serial_numbers: search.serial_numbers,
      ...(this.onlyDelivery
        ? { related_delivery_id: this.related_delivery_id! }
        : {
            related_warehouse_id: this.warehouse_id,
            purpose: this.purpose,
            ...(this.related_agreeement_id && {
              related_agreement_id: this.related_agreeement_id,
            }),
          }),
    };
    // if (event.globalFilter != null) {
    //   if (event.globalFilter[0] != '') {
    //     params.serial_number = event.globalFilter[0];
    //   }
    // }
    this.loadSub?.unsubscribe();
    this.loadSub = this.assetMgmtService.assetManagementControllerGetAll(params).subscribe({
      next: res => {
        this.assets = res.data.list;
        this.totalRecords = res.data.count;
        this.loading = false;
      },
      error: () => {
        this.loading = false;
      },
    });
  }
  close() {
    this.ref.close();
  }
  save() {
    this.ref.close({ selectedAssets: this.selectedAssets });
  }

  // Perché un asset non è selezionabile (null se lo è): un asset già partito con un DDT di
  // consegna non è movimentabile (quello di un trasferimento sì), né uno di un'altra consegna.
  blockReason = (asset: AssetManagementItem): string | null => {
    if (asset.related_ddt_id && !asset.related_ddt_is_transfer) {
      return 'Già partito con un DDT di consegna';
    }
    if (asset.related_delivery_id && asset.related_delivery_id !== this.related_delivery_id) {
      return `Già assegnato alla consegna #${asset.related_delivery_id}`;
    }
    return null;
  };

  isRowSelectable = (event: TableRowSelectEvent) => !this.blockReason(event.data);

  get selectedCount() {
    return this.selectedAssets.length;
  }

  // Quanti seriali si possono scegliere in questa finestra: la quantità evadibile meno quelli già
  // inseriti a mano (che non fanno parte della selezione della finestra). Senza limite: undefined.
  get maxSelectable(): number | undefined {
    return this.qtyLimit == null ? undefined : Math.max(0, this.qtyLimit - this.manualCount);
  }

  get limitReached() {
    return this.maxSelectable != null && this.selectedCount >= this.maxSelectable;
  }

  // già oltre la quantità (selezione preesistente): si avvisa, non si toglie nulla
  get overQtyLimit() {
    return this.qtyLimit != null && this.selectedCount + this.manualCount > this.qtyLimit;
  }

  isSelected(asset: AssetManagementItem) {
    return this.selectedAssets.some(a => a.id === asset.id);
  }

  // Oltre il massimo non si aggiunge nulla: restano i già selezionati e, dei nuovi, solo quelli
  // che ci stanno (vale anche per la selezione di tutta la pagina dalla casella in intestazione)
  onSelectionChange(next: AssetManagementItem[]) {
    const max = this.maxSelectable;
    if (max == null || next.length <= max) {
      this.selectedAssets = next;
      return;
    }
    const previous = new Set(this.selectedAssets.map(a => a.id));
    const kept = next.filter(a => previous.has(a.id));
    const added = next.filter(a => !previous.has(a.id));
    this.selectedAssets = [...kept, ...added.slice(0, Math.max(0, max - kept.length))];
  }

  get pasteCount() {
    return parseSerialList(this.pasteText.value).serials.length;
  }

  togglePaste() {
    this.pasteOpen = !this.pasteOpen;
  }

  closePaste() {
    this.pasteOpen = false;
  }

  clearPasteReport() {
    this.pasteReport = undefined;
    this.pasteOverLimit = [];
    this.pasteText.setValue('');
    this.pasteError = false;
  }

  private searchBySerials(serials: string[], scoped: boolean): Promise<AssetManagementItem[]> {
    const scope = this.pasteScope;
    const requests = chunk(serials, SERIAL_PASTE_CHUNK_SIZE).map(serial_numbers =>
      firstValueFrom(
        this.assetMgmtService.assetManagementControllerGetAll({
          // 0 = senza limite: ogni blocco ha già un numero contenuto di seriali
          limit: 0,
          skip: 0,
          serial_numbers,
          ...(scoped && {
            related_product_id: scope.product_id,
            ...(scope.delivery_id != null
              ? { related_delivery_id: scope.delivery_id }
              : {
                  related_warehouse_id: scope.warehouse_id,
                  purpose: scope.purpose,
                  ...(scope.agreement_id && { related_agreement_id: scope.agreement_id }),
                }),
          }),
        })
      )
    );
    return Promise.all(requests).then(responses => responses.flatMap(r => r.data.list));
  }

  private get pasteScope(): SerialPasteScope {
    if (this.onlyDelivery) {
      return {
        product_id: this.product_id,
        delivery_id: this.related_delivery_id ?? undefined,
      };
    }
    return {
      warehouse_id: this.warehouse_id,
      product_id: this.product_id,
      purpose: this.purpose,
      agreement_id: this.related_agreeement_id,
    };
  }

  // Cerca i seriali incollati e seleziona quelli trovati; per gli altri spiega il motivo
  async applyPastedList() {
    const parsed = parseSerialList(this.pasteText.value);
    if (parsed.serials.length === 0 || this.pasting) {
      return;
    }
    this.pasting = true;
    this.pasteError = false;
    this.notFoundCopied = false;
    try {
      const inScope = await this.searchBySerials(parsed.serials, true);
      const scopedKeys = new Set(inScope.map(a => a.serial_number.toUpperCase()));
      const missing = parsed.serials.filter(s => !scopedKeys.has(s.toUpperCase()));
      const elsewhere = missing.length > 0 ? await this.searchBySerials(missing, false) : [];
      const report = classifyPastedSerials({
        parsed,
        inScope,
        elsewhere,
        scope: this.pasteScope,
        blockReason: this.blockReason,
      });
      const alreadySelected = new Set(this.selectedAssets.map(a => a.id));
      const toAdd = report.selectable.filter(a => !alreadySelected.has(a.id));
      const max = this.maxSelectable;
      const room = max == null ? toAdd.length : Math.max(0, max - this.selectedAssets.length);
      this.selectedAssets = [...this.selectedAssets, ...toAdd.slice(0, room)];
      this.pasteOverLimit = toAdd.slice(room).map(a => a.serial_number);
      this.pasteReport = report;
    } catch {
      this.pasteError = true;
    } finally {
      this.pasting = false;
    }
  }

  copyNotFound() {
    const text = (this.pasteReport?.notFound ?? []).map(n => n.serial).join('\n');
    navigator.clipboard?.writeText(text).then(() => (this.notFoundCopied = true));
  }

  onPageChange(event: PaginatorState) {
    this.frm_search.get('skip')?.setValue(event.first || 0);
    this.frm_search.get('pageSize')?.setValue(event.rows || this.rowsPerPageOptions[0]);
    this.loadAssets();
  }
}
